<script setup lang="ts">
    import { ref, watch, computed } from 'vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import Modal from '@shared/components/Modal.vue';
    import Loading from '@shared/components/Loading.vue';
    import CronEditor from '@shared/components/RecurringEditor/CronEditor.vue';
    import type { TaskSchedule } from '@shared/components/RecurringEditor/CronProperties';
    import type { Issue } from '../../interfaces';
    import * as statsApi from '../IssueStatistics.access';
    import EmptyState from '@shared/components/EmptyState.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import cronstrue from 'cronstrue/i18n';
    import { formatDateTime } from '@shared/utils/formatDateTime';

    const props = defineProps<{
        show: boolean;
        issue: Issue;
    }>();

    const emit = defineEmits<{
        (e: 'close'): void;
    }>();

    const schedules = ref<TaskSchedule[]>([]);
    const selectedSchedule = ref<TaskSchedule | null>(null);
    const showEditor = ref(false);
    const cronExpression = ref('0 0 8 ? * MON *');
    const selectedStart = ref('');
    const timeZone = ref('');
    const nextDates = ref<string[]>([]);
    const resendToExistingRecipients = ref(false);
    const loading = ref(false);
    const saving = ref(false);

    watch(
        () => props.show,
        async (val) => {
            if (!val) return;
            loading.value = true;
            showEditor.value = false;
            try {
                schedules.value = await statsApi.getSchedules(props.issue.IssueId);
            } finally {
                loading.value = false;
            }
            if (!hasActiveSchedules.value) openCreate();
        }
    );

    const hasActiveSchedules = computed(() => schedules.value.some((s) => s.TaskStatus !== 'Aborted' && s.TaskStatus !== 'Ended'));

    const resendStr = computed({
        get: () => String(resendToExistingRecipients.value),
        set: (val: string) => {
            resendToExistingRecipients.value = val === 'true';
        },
    });

    function openCreate() {
        selectedSchedule.value = null;
        cronExpression.value = '0 0 8 ? * MON *';
        selectedStart.value = '';
        timeZone.value = '';
        nextDates.value = [];
        resendToExistingRecipients.value = props.issue.ResendToExistingRecipients;
        showEditor.value = true;
    }

    function openEdit(schedule: TaskSchedule) {
        selectedSchedule.value = schedule;
        cronExpression.value = schedule.CronExpression || '0 0 8 ? * MON *';
        selectedStart.value = (schedule.Scheduled as string) || '';
        timeZone.value = schedule.TimeZone || '';
        nextDates.value = [];
        resendToExistingRecipients.value = schedule.ResendToExistingRecipients ?? false;
        showEditor.value = true;
    }

    async function save() {
        saving.value = true;
        try {
            const schedule: TaskSchedule = {
                ...(selectedSchedule.value ?? {}),
                ObjectId: props.issue.IssueId,
                CronExpression: cronExpression.value,
                Scheduled: selectedStart.value || null,
                TimeZone: timeZone.value,
                TaskType: 'SendIssue',
                TaskStatus: 'Scheduled',
                IsRecurring: true,
                ResendToExistingRecipients: resendToExistingRecipients.value,
            };
            await statsApi.saveSchedule(props.issue.IssueId, schedule);
            bsAlert({ type: AlertTypes.Success, content: $Resources.AlertSuccessHeading });
            schedules.value = await statsApi.getSchedules(props.issue.IssueId);
            showEditor.value = false;
            selectedSchedule.value = null;
        } catch {
            bsAlert({ type: AlertTypes.Danger, content: $Resources.ErrorHeader });
        } finally {
            saving.value = false;
        }
    }

    async function abort(schedule: TaskSchedule) {
        try {
            await statsApi.saveSchedule(props.issue.IssueId, { ...schedule, TaskStatus: 'Aborted' });
            schedules.value = await statsApi.getSchedules(props.issue.IssueId);
        } catch {
            bsAlert({ type: AlertTypes.Danger, content: $Resources.ErrorHeader });
        }
    }

    function isSelected(schedule: TaskSchedule): boolean {
        return selectedSchedule.value?.TaskId === schedule.TaskId;
    }

    function cronLabel(expression: string): string {
        try {
            return cronstrue.toString(expression, { locale: appconfig?.lang?.loc });
        } catch {
            return expression;
        }
    }

    const cancel = () => {
        showEditor.value = false;
        selectedSchedule.value = null;
    };
</script>

<template>
    <Modal :show="show" :title="$Resources.RecurringEditorTitle" size="xl" @close="emit('close')">
        <template #content>
            <Loading :show="loading" loadingStyle="progress-bar" />

            <template v-if="!loading">
                <FormRadioGroup
                    v-backoffice
                    :legend="null"
                    :options="[
                        { label: $Resources.RecurringEditorResendToExistingFalseLabel, value: 'false' },
                        { label: $Resources.RecurringEditorResendToExistingFalseTrue, value: 'true' },
                    ]"
                    v-model="resendStr" />

                <table v-if="schedules.length > 0" class="table table-hover">
                    <thead>
                        <tr>
                            <th>{{ $Resources.RecurringEditorStatusTableHeader }}</th>
                            <th>{{ $Resources.RecurringEditorScheduledTableHeader }}</th>
                            <th>{{ $Resources.RecurringEditorCronExpressionTableHeader }}</th>
                            <th class="autofit"></th>
                            <th class="autofit"></th>
                        </tr>
                    </thead>
                    <tbody class="td-align-middle">
                        <tr v-for="schedule in schedules" :key="schedule.TaskId" :class="{ 'bg-primary c-white': isSelected(schedule) }">
                            <td class="text-nowrap">{{ $Resources['TaskStatus' + schedule.TaskStatus] }}</td>
                            <td class="text-nowrap">{{ schedule.Scheduled ? formatDateTime(schedule.Scheduled) : '' }}</td>
                            <td>{{ schedule.CronExpression ? cronLabel(schedule.CronExpression) : '' }}</td>
                            <td class="text-right">
                                <button class="btn btn-default btn-sm" @click="openEdit(schedule)">
                                    {{ $Resources.RecurringEditorEditLabel }}
                                </button>
                            </td>
                            <td class="text-right">
                                <ConfirmButton
                                    v-if="schedule.TaskStatus === 'Scheduled'"
                                    :disabled="showEditor"
                                    buttonClasses="btn-sm"
                                    :buttonText="$Resources.RecurringEditorAbortTask"
                                    @confirm="abort(schedule)" />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-show="!showEditor">
                    <div v-if="hasActiveSchedules" class="form-group">
                        <button class="btn btn-primary" @click="openCreate">
                            {{ $Resources.RecurringEditorAddNewScheduleButton }}
                        </button>
                    </div>

                    <EmptyState v-else :title="$Resources.RecurringEditorNoScheduleHeader" :body="$Resources.RecurringEditorAddScheduleParagraph">
                        <template #actions>
                            <button type="button" class="btn btn-info" @click="openCreate">
                                {{ $Resources.RecurringEditorAddNewScheduleButton }}
                            </button>
                        </template>
                    </EmptyState>
                </div>

                <div v-show="showEditor">
                    <h3>{{ $Resources.RecurringEditorEditLabel }}</h3>

                    <CronEditor
                        :key="selectedSchedule?.TaskId ?? 'new'"
                        :showExecutionHistoryButton="false"
                        :hideHourlyOption="true"
                        v-model:cronExpression="cronExpression"
                        v-model:timeZone="timeZone"
                        v-model:selectedStart="selectedStart"
                        v-model:nextDates="nextDates" />

                    <div class="form-group d-flex flex-wrap gap-xs">
                        <button class="btn btn-subtle" @click="cancel">
                            {{ $Resources.BackButton }}
                        </button>
                        <button class="btn btn-primary" :disabled="saving" @click="save">
                            {{ selectedSchedule?.TaskStatus === 'Aborted' ? $Resources.RecurringEditorSaveAndScheduleButton : $Resources.SaveButton }}
                        </button>
                    </div>
                </div>
            </template>
        </template>
    </Modal>
</template>

